/**
 * Configuration SEO pour Framerie
 * Application de capture d'écran moderne par Dreambel Tech
 * Développée par Guénolé KIKABOU KIA MOUKALA
 */

import { envConfig } from './env-config';

export const seoConfig = {
  // Informations principales
  siteName: 'Framerie',
  siteDescription: 'Modern screenshot application. Capture, record and share your screens effortlessly.',
  siteUrl: envConfig.siteUrl,
  
  // Développeur & Entreprise
  author: {
    name: 'Guénolé KIKABOU KIA MOUKALA ',
    company: 'Dreambel Tech',
    email: 'contact@dreambel.tech',
    instagram: 'https://www.instagram.com/guenolekikabou',
    linkedin: 'https://www.linkedin.com/in/guenole/',
    website: 'https://dreambel.tech'
  },
  
  // Mots-clés SEO principaux (English + French)
  keywords: [
    // Application & Features (English)
    'Framerie',
    'screenshot',
    'screen capture',
    'screen recording',
    'video capture',
    'GIF maker',
    'capture tool',
    'screenshot app',
    'screen recorder',
    'screenshot software',
    
    // Application & Fonctionnalités (French)
    'capture écran',
    'application capture',
    'enregistrement vidéo',
    'capture vidéo',
    'outil capture',
    'logiciel screenshot',
    
    // Developer & Company
    'Guénolé KIKABOU KIA MOUKALA',
    'Guénolé',
    'KIKABOU',
    'KIKABOU KIA MOUKALA ',
    'Dreambel',
    'Dreambel Tech',
    
    // Technical & Development (English)
    'dev application',
    'developer tools',
    'software development',
    'productivity app',
    'desktop application',
    'modern app',
    
    // Technical & Development (French)
    'application développement',
    'outil développeur',
    'dev tools',
    
    // Platforms (English)
    'Windows app',
    'Windows software',
    'Windows screenshot',
    'macOS app',
    'macOS software',
    'Linux app',
    'Linux software',
    'cross-platform',
    'multi-platform',
    
    // Platforms (French)
    'application Windows',
    'logiciel Windows',
    'capture Windows',
    'application macOS',
    'logiciel macOS',
    'application Linux',
    'logiciel Linux',
    'multi-plateforme',
    
    // Interface & Experience (English)
    'modern interface',
    'fluid application',
    'professional tool',
    'professional capture',
    'intuitive interface',
    'modern design',
    'user-friendly',
    'easy to use',
    
    // Interface & Experience (French)
    'interface moderne',
    'application fluide',
    'outil professionnel',
    'capture professionnelle',
    'interface intuitive',
    'design moderne',
    
    // Performance & Quality (English)
    '60fps',
    'high quality',
    'instant capture',
    'optimized performance',
    'smooth',
    'fast capture',
    
    // Performance & Quality (French)
    'haute qualité',
    'capture instantanée',
    'performance optimisée',
    'fluidité',
    
    // Capture Types (English)
    'region capture',
    'area selection',
    'fullscreen',
    'active window',
    'clipboard',
    'auto copy',
    
    // Capture Types (French)
    'capture région',
    'sélection zone',
    'plein écran',
    'fenêtre active',
    'presse-papiers',
    'copie automatique'
  ],
  
  // Pages principales pour SEO (English + French)
  pages: {
    home: {
      title: 'Framerie',
      titleShort: 'Framerie',
      description: 'The screenshot tool that revolutionizes your workflow. Capture, record and share with one click.',
      descriptionFr: 'L\'outil de capture d\'écran qui révolutionne votre workflow. Capturez, enregistrez et partagez en un clic.',
      path: '/',
      get url() { return `${envConfig.siteUrl}/`; }
    },
    features: {
      title: 'Framerie - Features',
      titleFr: 'Framerie - Fonctionnalités',
      description: 'Discover all Framerie features: instant capture, 60fps video recording, GIF creation and more.',
      descriptionFr: 'Découvrez toutes les fonctionnalités de Framerie : capture instantanée, enregistrement vidéo 60fps, création de GIF.',
      path: '/features',
      get url() { return `${envConfig.siteUrl}/features`; }
    },
    pricing: {
      title: 'Framerie - Pricing',
      titleFr: 'Framerie - Tarifs',
      description: 'Framerie pricing plans: complete free version and premium with advanced features.',
      descriptionFr: 'Plans tarifaires Framerie : version gratuite complète et premium avec fonctionnalités avancées.',
      path: '/pricing',
      get url() { return `${envConfig.siteUrl}/pricing`; }
    },
    download: {
      title: 'Framerie - Download',
      titleFr: 'Framerie - Télécharger',
      description: 'Download Framerie for free on Windows, macOS and Linux. Modern and intuitive application.',
      descriptionFr: 'Téléchargez Framerie gratuitement sur Windows, macOS et Linux. Application moderne et intuitive.',
      path: '/download',
      get url() { return `${envConfig.siteUrl}/download`; }
    },
    demo: {
      title: 'Framerie - Demo',
      titleFr: 'Framerie - Démo',
      description: 'See Framerie in action: fluid interface, instant captures, high quality videos.',
      descriptionFr: 'Découvrez Framerie en action : interface fluide, captures instantanées, vidéos haute qualité.',
      path: '/demo',
      get url() { return `${envConfig.siteUrl}/demo`; }
    }
  },
  
  // Configuration Open Graph
  openGraph: {
    type: 'website',
    locale: 'en_US',
    alternateLocales: ['fr_FR'],
    siteName: 'Framerie',
    images: {
      default: '/assets/icons/android-icon-192x192.png',
      twitter: '/assets/icons/android-icon-192x192.png',
      width: 192,
      height: 192
    }
  },
  
  // Configuration Twitter
  twitter: {
    cardType: 'summary_large_image',
    site: '@framerie_app',
    creator: '@dreambel_tech'
  },
  
  // Configuration technique
  technical: {
    language: 'en',
    alternateLanguages: ['fr'],
    robotsPolicy: 'index, follow',
    canonicalUrl: envConfig.siteUrl
  },
  
  // Données structurées (Schema.org)
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    applicationCategory: 'UtilitiesApplication',
    applicationSubCategory: 'Screen Capture',
    operatingSystem: ['Windows', 'macOS', 'Linux']
  }
};

export default seoConfig;
